/**
 * Combined Step: UBO Ownership Trace + Biometric Verification + Authorized Person + Declarations
 *
 * Flow:
 * 1. Show ownership tree / UBO analysis + list ALL persons requiring Sumsub (Directors, UBOs ≥25%, Auth Person)
 *    - Tap person → inline Sumsub verification on this device
 *    - Send link via Email / SMS / WhatsApp for remote verification
 *    - Continue blocked until ALL persons are verified (green tick)
 * 2. For UBOs traced from corporate shareholders, collect inline declarations
 * 3. Select / add Authorized Person
 * 4. Collect Authorized Person PEP + Tax + Non-US declarations
 * 5. Continue to documents
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useOnboarding } from '../OnboardingContext';
import {
  AlertCircle, ChevronRight, Building2, User, Users,
  ChevronDown, ChevronUp, Loader2, Check, Plus, X,
  Search, Globe, Shield, UserCheck, UserPlus,
  Mail, MessageSquare, Phone, Send, BadgeCheck, Fingerprint, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { calculateEffectiveOwnership, type EffectiveOwner } from '@/utils/effectiveOwnership';
import { countriesSorted } from '@/data/countries';
import { validateTIN, getTINPlaceholder, getTINHint } from '@/utils/tinValidation';
import { PEPDeclarationForm } from './PEPDeclarationForm';
import { phoneCodes, phoneCodesSorted } from '@/data/phoneCodes';
import { countries } from '@/data/countries';
import { SumsubVerification, type ExtractedData } from '../SumsubVerification';
import { VerificationConfirmScreen } from '../VerificationConfirmScreen';
import { supabase } from '@/integrations/supabase/client';
import { FlagImg } from '@/components/ui/CountryFlag';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { formatLabel } from '@/lib/format-labels';



/* ─── Phone country codes for send-link panel ──────────────────── */
const PHONE_CODES = [
  { code: '+44', flag: '🇬🇧', country: 'United Kingdom', iso2: 'GB' },
  { code: '+1',  flag: '🇺🇸', country: 'United States', iso2: 'US' },
  { code: '+351', flag: '🇵🇹', country: 'Portugal', iso2: 'PT' },
  { code: '+33', flag: '🇫🇷', country: 'France', iso2: 'FR' },
  { code: '+49', flag: '🇩🇪', country: 'Germany', iso2: 'DE' },
  { code: '+34', flag: '🇪🇸', country: 'Spain', iso2: 'ES' },
  { code: '+39', flag: '🇮🇹', country: 'Italy', iso2: 'IT' },
  { code: '+31', flag: '🇳🇱', country: 'Netherlands', iso2: 'NL' },
  { code: '+32', flag: '🇧🇪', country: 'Belgium', iso2: 'BE' },
  { code: '+41', flag: '🇨🇭', country: 'Switzerland', iso2: 'CH' },
  { code: '+353', flag: '🇮🇪', country: 'Ireland', iso2: 'IE' },
  { code: '+45', flag: '🇩🇰', country: 'Denmark', iso2: 'DK' },
  { code: '+46', flag: '🇸🇪', country: 'Sweden', iso2: 'SE' },
  { code: '+47', flag: '🇳🇴', country: 'Norway', iso2: 'NO' },
  { code: '+358', flag: '🇫🇮', country: 'Finland', iso2: 'FI' },
  { code: '+48', flag: '🇵🇱', country: 'Poland', iso2: 'PL' },
  { code: '+43', flag: '🇦🇹', country: 'Austria', iso2: 'AT' },
  { code: '+30', flag: '🇬🇷', country: 'Greece', iso2: 'GR' },
  { code: '+420', flag: '🇨🇿', country: 'Czech Republic', iso2: 'CZ' },
  { code: '+36', flag: '🇭🇺', country: 'Hungary', iso2: 'HU' },
  { code: '+40', flag: '🇷🇴', country: 'Romania', iso2: 'RO' },
  { code: '+359', flag: '🇧🇬', country: 'Bulgaria', iso2: 'BG' },
  { code: '+385', flag: '🇭🇷', country: 'Croatia', iso2: 'HR' },
  { code: '+421', flag: '🇸🇰', country: 'Slovakia', iso2: 'SK' },
  { code: '+386', flag: '🇸🇮', country: 'Slovenia', iso2: 'SI' },
  { code: '+370', flag: '🇱🇹', country: 'Lithuania', iso2: 'LT' },
  { code: '+371', flag: '🇱🇻', country: 'Latvia', iso2: 'LV' },
  { code: '+372', flag: '🇪🇪', country: 'Estonia', iso2: 'EE' },
  { code: '+357', flag: '🇨🇾', country: 'Cyprus', iso2: 'CY' },
  { code: '+356', flag: '🇲🇹', country: 'Malta', iso2: 'MT' },
  { code: '+352', flag: '🇱🇺', country: 'Luxembourg', iso2: 'LU' },
  { code: '+90', flag: '🇹🇷', country: 'Turkey', iso2: 'TR' },
  { code: '+971', flag: '🇦🇪', country: 'UAE', iso2: 'AE' },
  { code: '+91',  flag: '🇮🇳', country: 'India', iso2: 'IN' },
  { code: '+86',  flag: '🇨🇳', country: 'China', iso2: 'CN' },
  { code: '+81',  flag: '🇯🇵', country: 'Japan', iso2: 'JP' },
  { code: '+55',  flag: '🇧🇷', country: 'Brazil', iso2: 'BR' },
  { code: '+61',  flag: '🇦🇺', country: 'Australia', iso2: 'AU' },
  { code: '+65',  flag: '🇸🇬', country: 'Singapore', iso2: 'SG' },
  { code: '+27',  flag: '🇿🇦', country: 'South Africa', iso2: 'ZA' },
  { code: '+52',  flag: '🇲🇽', country: 'Mexico', iso2: 'MX' },
  { code: '+966', flag: '🇸🇦', country: 'Saudi Arabia', iso2: 'SA' },
  { code: '+972', flag: '🇮🇱', country: 'Israel', iso2: 'IL' },
];

/* ─── KycPerson type for biometrics list ────────────────────────── */
interface KycPerson {
  id: string;
  name: string;
  role: string;
  type: 'director' | 'ubo' | 'authorized';
  effectiveOwnership?: number;
  email: string;
  phone: string;
  completed: boolean;
  linkSentVia?: 'email' | 'sms' | 'whatsapp';
  linkSentAt?: string;
  sendPanelOpen: 'email' | 'sms' | 'whatsapp' | null;
  sending: 'email' | 'sms' | 'whatsapp' | null;
  saving: boolean;
  // Confirmed KYC fields saved from VerificationConfirmScreen
  confirmedData?: import('../VerificationConfirmScreen').ConfirmedPersonData;
}

/* ─── PhoneInputSmall ───────────────────────────────────────────── */
function PhoneInputSmall({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [countryCode, setCountryCode] = useState('+44');
  const [number, setNumber] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!value) return;
    // Sort by longest code first to avoid partial matches (e.g. +3 before +359)
    const sorted = [...PHONE_CODES].sort((a, b) => b.code.length - a.code.length);
    const matched = sorted.find(c => value.startsWith(c.code));
    if (matched) { setCountryCode(matched.code); setNumber(value.slice(matched.code.length).trim()); }
    else setNumber(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (code: string, num: string) => onChange(`${code}${num}`);
  const filtered = search.trim() ? PHONE_CODES.filter(c => c.country.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search)) : [];
  const selected = PHONE_CODES.find(c => c.code === countryCode) || PHONE_CODES[0];

  return (
    <div className="space-y-1 w-full">
      <div className="flex gap-2">
        <button type="button" onClick={() => { setOpen(v => !v); setSearch(''); }}
          className="flex items-center gap-1 px-2 h-9 rounded-lg border border-input bg-background hover:bg-muted/40 transition-colors text-xs whitespace-nowrap shrink-0">
          <FlagImg code={selected.iso2} size="xs" />
          <span className="font-medium">{selected.code}</span>
          <ChevronDown className={cn('w-3 h-3 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
        <Input type="tel" value={number}
          onChange={e => { const n = e.target.value.replace(/\D/g, '').slice(0, 15); setNumber(n); emit(countryCode, n); }}
          placeholder="Mobile number" className="h-9 text-sm flex-1" />
      </div>
      {open && (
        <div className="rounded-xl border border-border bg-popover overflow-hidden w-56 shadow-lg z-20">
          <input type="text" placeholder="Search country..." value={search} autoFocus
            onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-muted/40 border-b border-border focus:outline-none placeholder:text-muted-foreground" style={{ fontSize: '16px' }} />
          <div className="max-h-36 overflow-y-auto">
            {search.trim() === '' ? (
              <p className="px-3 py-2 text-xs text-muted-foreground text-center">Start typing to search…</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground text-center">No results</p>
            ) : filtered.slice(0, 20).map((c, i) => (
              <button key={`${c.code}-${i}`} type="button"
                onClick={() => { setCountryCode(c.code); emit(c.code, number); setOpen(false); setSearch(''); }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/60 transition-colors text-left text-xs border-b border-border/30 last:border-0">
                <FlagImg code={c.iso2} size="xs" />
                <span className="flex-1 truncate text-foreground">{c.country}</span>
                <span className="text-muted-foreground font-medium shrink-0">{c.code}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Types ────────────────────────────────────────────────────────────────────

interface TaxResidency { code: string; name: string; flag: string; tin?: string; }
interface TinError { code: string; error?: string; }

interface PersonDeclarationData {
  isPEP?: boolean;
  pepDetails?: string;
  taxResidencies?: TaxResidency[];
  isUSResident?: boolean;
  giinNumber?: string;
  declarationsCompleted?: boolean;
}

interface UBOWithDeclarations extends PersonDeclarationData {
  name: string;
  effectiveOwnership: number;
  path: string[];
}

interface AuthorizedPerson extends PersonDeclarationData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  phoneCountryCode: string;
  jobTitle: string;
  dateOfBirth: string;
  nationality: string;
  address: { street: string; city: string; postalCode: string; country: string; };
  isDirector: boolean;
}

interface UBO {
  name: string;
  shareholding: number;
  address: { street: string; city: string; postalCode: string; country: string; };
  type: 'individual' | 'company';
  registrationNumber?: string;
  ubos?: UBO[];
}

interface Shareholder {
  name: string;
  shareholding: number;
  shareClass: string;
  address: { street: string; city: string; postalCode: string; country: string; };
  type: 'individual' | 'company';
  ubos?: UBO[];
  registrationNumber?: string;
}

type MainStage = 'ubo-trace' | 'ubo-declarations';

// ── Country/phone helpers ─────────────────────────────────────────────────────
const countryToPhoneCode: Record<string, string> = {
  'PT': '+351', 'GB': '+44', 'IE': '+353', 'US': '+1', 'CA': '+1',
  'DE': '+49', 'FR': '+33', 'ES': '+34', 'IT': '+39', 'NL': '+31',
  'BE': '+32', 'AT': '+43', 'CH': '+41', 'PL': '+48', 'SE': '+46',
  'NO': '+47', 'DK': '+45', 'FI': '+358', 'GR': '+30', 'CZ': '+420',
  'HU': '+36', 'RO': '+40', 'BG': '+359', 'HR': '+385', 'SK': '+421',
  'SI': '+386', 'LT': '+370', 'LV': '+371', 'EE': '+372',
  'CY': '+357', 'MT': '+356', 'LU': '+352', 'AU': '+61', 'NZ': '+64',
  'JP': '+81', 'KR': '+82', 'CN': '+86', 'HK': '+852', 'SG': '+65',
  'AE': '+971', 'SA': '+966', 'ZA': '+27', 'BR': '+55', 'MX': '+52',
};

const resolveCountryName = (val: string): string => {
  if (!val) return '';
  const c = countries.find(x => x.code.toUpperCase() === val.toUpperCase());
  return c?.name || val;
};

// ── Inline declarations sub-component ────────────────────────────────────────
interface InlineDeclarationsProps {
  person: PersonDeclarationData & { name: string };
  onChange: (updates: Partial<PersonDeclarationData>) => void;
  onPEPFormOpen?: () => void;
  onPEPFormClose?: () => void;
  showPEPForm?: boolean;
}

function InlineDeclarations({ person, onChange, onPEPFormOpen, onPEPFormClose, showPEPForm = false }: InlineDeclarationsProps) {
  const [localShowPEPForm, setLocalShowPEPForm] = useState(false);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [tinErrors, setTinErrors] = useState<TinError[]>([]);

  const taxResidencies = person.taxResidencies || [];

  const filteredCountries = countriesSorted.filter(
    c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) &&
      !taxResidencies.some(t => t.code === c.code)
  );

  const addCountry = (c: { code: string; name: string; flag: string }) => {
    onChange({ taxResidencies: [...taxResidencies, { ...c, tin: '' }] });
    if (c.code === 'US') onChange({ isUSResident: true });
    setShowCountryPicker(false);
    setCountrySearch('');
  };

  const removeCountry = (code: string) => {
    onChange({ taxResidencies: taxResidencies.filter(t => t.code !== code) });
    setTinErrors(prev => prev.filter(e => e.code !== code));
    if (code === 'US') onChange({ isUSResident: false, giinNumber: '' });
  };

  const updateTIN = (code: string, tin: string) => {
    onChange({ taxResidencies: taxResidencies.map(t => t.code === code ? { ...t, tin } : t) });
    if (tin.trim()) {
      const result = validateTIN(code, tin);
      if (!result.isValid) setTinErrors(prev => [...prev.filter(e => e.code !== code), { code, error: result.error }]);
      else setTinErrors(prev => prev.filter(e => e.code !== code));
    } else setTinErrors(prev => prev.filter(e => e.code !== code));
  };

  if (localShowPEPForm) {
    return (
      <PEPDeclarationForm
        onBack={() => setLocalShowPEPForm(false)}
        onComplete={() => {
          onChange({ pepDetails: 'Completed via LMAML form' });
          setLocalShowPEPForm(false);
        }}
        personName={person.name}
        personType="UBO / Director"
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* PEP */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-primary" />
          <p className="text-sm font-semibold">PEP Declaration</p>
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Is this person a Politically Exposed Person or related to one?
        </p>
        <div className="grid grid-cols-2 gap-2">
          {([false, true] as const).map(val => (
            <button key={String(val)}
              onClick={() => {
                onChange({ isPEP: val });
                if (val) setLocalShowPEPForm(true);
              }}
              className={`py-2.5 rounded-xl border text-xs font-medium transition-colors ${
                person.isPEP === val
                  ? val ? 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'border-success bg-success/10 text-success'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              {val ? 'Yes — PEP' : 'No — Not a PEP'}
            </button>
          ))}
        </div>
        {person.isPEP === true && person.pepDetails && (
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Check className="w-3 h-3 text-success" /> PEP form completed
          </p>
        )}
      </div>

      {/* Tax Residency */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-4 h-4 text-primary" />
          <p className="text-sm font-semibold">Tax Residency & TIN</p>
        </div>
        <div className="space-y-2 mb-2">
          {taxResidencies.map(country => {
            const tinError = tinErrors.find(e => e.code === country.code);
            const hint = getTINHint(country.code);
            return (
              <div key={country.code} className="p-2 rounded-lg border border-border bg-muted/20">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm flex items-center gap-1.5"><FlagImg code={country.code} size="xs" />{country.name}</span>
                  <button onClick={() => removeCountry(country.code)} className="p-1 hover:bg-muted rounded">
                    <X className="w-3 h-3 text-muted-foreground" />
                  </button>
                </div>
                {hint && <p className="text-xs text-muted-foreground mb-1">Format: {hint}</p>}
                <Input value={country.tin || ''} onChange={e => updateTIN(country.code, e.target.value)}
                  placeholder={getTINPlaceholder(country.code)}
                  className={`h-8 text-xs ${tinError ? 'border-destructive' : ''}`} />
                {tinError && <p className="text-xs text-destructive mt-0.5">{tinError.error}</p>}
              </div>
            );
          })}
        </div>
        {showCountryPicker ? (
          <div className="space-y-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input type="text" value={countrySearch} onChange={e => setCountrySearch(e.target.value)}
                placeholder="Search country..." autoFocus
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-muted/30 border border-border focus:outline-none" />
            </div>
            <div className="max-h-28 overflow-y-auto border border-border rounded-lg">
              {filteredCountries.slice(0, 8).map(c => (
                <button key={c.code} onClick={() => addCountry(c)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 text-left text-xs border-b border-border last:border-b-0">
                  <FlagImg code={c.code} size="xs" />{c.name}
                </button>
              ))}
              {filteredCountries.length === 0 && (
                <p className="text-center py-3 text-xs text-muted-foreground">No countries found</p>
              )}
            </div>
            <button onClick={() => { setShowCountryPicker(false); setCountrySearch(''); }}
              className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setShowCountryPicker(true)}
            className="w-full h-9 rounded-xl border-2 border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1 text-xs">
            <Plus className="w-3 h-3" /> Add Tax Country
          </button>
        )}
      </div>

      {/* FATCA / Non-US */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-4 h-4 text-primary" />
          <p className="text-sm font-semibold">US Person Declaration (FATCA)</p>
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Is this person a US citizen, US resident, or subject to US tax reporting?
        </p>
        <div className="grid grid-cols-2 gap-2">
          {([false, true] as const).map(val => (
            <button key={String(val)} onClick={() => onChange({ isUSResident: val })}
              className={`py-2.5 rounded-xl border text-xs font-medium transition-colors ${
                person.isUSResident === val
                  ? val ? 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'border-success bg-success/10 text-success'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              {val ? 'Yes — US Person' : 'No — Not a US Person'}
            </button>
          ))}
        </div>
        {person.isUSResident === true && (
          <div className="mt-2">
            <p className="text-xs text-muted-foreground mb-1">GIIN Number (if applicable):</p>
            <Input value={person.giinNumber || ''} onChange={e => onChange({ giinNumber: e.target.value })}
              placeholder="e.g. XXXXXX.XXXXX.SL.XXX" className="h-8 text-xs" />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Role pill colour helper ───────────────────────────────────────────────────
const getRolePillClass = (role: string): string => {
  const r = role.toLowerCase();
  if (r.includes('director')) return 'bg-blue-500/15 text-blue-400 border-blue-500/30';
  if (r.includes('shareholder') || r.includes('ubo') || r.includes('%')) return 'bg-violet-500/15 text-violet-400 border-violet-500/30';
  if (r.includes('authoris') || r.includes('authoriz')) return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
  return 'bg-muted/40 text-muted-foreground border-border/50';
};

// ── Main Component ────────────────────────────────────────────────────────────
export function CorporateUBOTraceStep() {
  const { data, updateData, goToNextStep, setCustomBackHandler, sessionToken } = useOnboarding();
  const { toast } = useToast();
  const company = data.corporateCompany;
  const shareholders = company?.shareholders || [];

  // Authorized person check (must be done before useState that references it)
  const alreadySaved = Boolean(company?.authorizedPerson?.firstName && company?.authorizedPerson?.lastName);
  const authDeclarationsAlreadyDone = alreadySaved && Boolean(
    (company?.authorizedPerson as any)?.isPEP !== undefined &&
    ((company?.authorizedPerson as any)?.taxResidencies?.length > 0)
  );

  const [stage, setStage] = useState<MainStage>('ubo-trace');
  const [authSumsubExtracted, setAuthSumsubExtracted] = useState<ExtractedData | undefined>(undefined);
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [effectiveOwners, setEffectiveOwners] = useState<EffectiveOwner[]>([]);
  const [traceComplete, setTraceComplete] = useState(false);
  const [incompleteChains, setIncompleteChains] = useState<string[]>([]);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  // UBOs from corporate shareholders (not already covered as individual shareholders)
  const [tracedUBOs, setTracedUBOs] = useState<UBOWithDeclarations[]>([]);
  const [activeUBOIndex, setActiveUBOIndex] = useState(0);

  // ── Biometrics / Sumsub persons state ───────────────────────────────────────
  const [kycPersons, setKycPersons] = useState<KycPerson[]>([]);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [forceConfirmPerson, setForceConfirmPerson] = useState<KycPerson | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{ id: string; extractedData?: ExtractedData } | null>(null);
  // Fresh unique Sumsub person ID per verification attempt — stored in a ref so it's
  // stable for the lifetime of the verifyingId state (no sessionStorage, no caching).
  const sumsubPersonIdRef = useRef<string | null>(null);
  const lastVerifyingIdRef = useRef<string | null>(null);
  // DB-sourced enriched biometrics (with KYC fields like firstName, dateOfBirth, etc.)
  const [dbBiometrics, setDbBiometrics] = useState<Record<string, unknown>[]>([]);

  // Fetch fresh enriched biometrics from DB and update context + local state
  const refreshDbBiometrics = async () => {
    const dbSessionId = await getSessionDbId();
    if (!dbSessionId) return;
    const { data: sess } = await supabase
      .from('onboarding_sessions')
      .select('onboarding_data')
      .eq('id', dbSessionId)
      .maybeSingle();
    if (sess?.onboarding_data) {
      const bio = ((sess.onboarding_data as any)?.corporateBiometrics || []) as any[];
      setDbBiometrics(bio);
      updateData({ corporateBiometrics: bio as any });
    }
  };

  // Authorized person
  const [authStage, setAuthStage] = useState<'selection' | 'sumsub' | 'confirm'>(alreadySaved ? 'confirm' : 'selection');
  const [selectionMode, setSelectionMode] = useState<'existing' | 'new' | null>(null);
  const [selectedExistingId, setSelectedExistingId] = useState<string | null>(null);
  const [existingPeople, setExistingPeople] = useState<{ id: string; name: string; type: 'director' | 'shareholder'; role?: string; shareholding?: number }[]>([]);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [showPhoneCountryPicker, setShowPhoneCountryPicker] = useState(false);
  const [filteredPhoneCodes, setFilteredPhoneCodes] = useState(phoneCodesSorted);

  const getDefaultPhoneCode = () => {
    if (company?.countryCode) return countryToPhoneCode[company.countryCode] || '+44';
    return data.countryCode || '+44';
  };

  const saved = company?.authorizedPerson as (typeof company.authorizedPerson & PersonDeclarationData) | undefined;
  const [authPerson, setAuthPerson] = useState<AuthorizedPerson>({
    firstName: saved?.firstName || '',
    lastName: saved?.lastName || '',
    email: saved?.email || data.email || '',
    phoneNumber: saved?.phoneNumber || data.phoneNumber || '',
    phoneCountryCode: (saved as any)?.phoneCountryCode || getDefaultPhoneCode(),
    jobTitle: saved?.jobTitle || '',
    dateOfBirth: saved?.dateOfBirth || '',
    nationality: saved?.nationality || '',
    address: saved?.address || { street: '', city: '', postalCode: '', country: '' },
    isDirector: saved?.isDirector || false,
    isPEP: (saved as any)?.isPEP,
    pepDetails: (saved as any)?.pepDetails,
    taxResidencies: (saved as any)?.taxResidencies || [],
    isUSResident: (saved as any)?.isUSResident,
    giinNumber: (saved as any)?.giinNumber || '',
  });

  // Helper: resolve the DB session row id using sessionToken or email fallback
  const getSessionDbId = async (): Promise<string | null> => {
    // Try by session_token inside onboarding_data JSONB
    if (sessionToken) {
      const { data: byToken } = await supabase
        .from('onboarding_sessions')
        .select('id')
        .filter('onboarding_data->>session_token', 'eq', sessionToken)
        .maybeSingle();
      if (byToken?.id) return byToken.id;
    }
    // Fallback: look up by email, pick most recently updated
    const email = data.email;
    if (email) {
      const { data: byEmail } = await supabase
        .from('onboarding_sessions')
        .select('id')
        .filter('onboarding_data->>email', 'eq', email)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (byEmail?.id) return byEmail.id;
    }
    return null;
  };

  // Analyze ownership
  useEffect(() => {
    const analyze = async () => {
      setIsAnalyzing(true);
      await new Promise(r => setTimeout(r, 800));
      const owners = calculateEffectiveOwnership(shareholders);
      setEffectiveOwners(owners);

      const incomplete: string[] = [];
      const check = (entities: (Shareholder | UBO)[], path = '') => {
        for (const e of entities) {
          if (e.type === 'company') {
            if (!e.ubos?.length) incomplete.push(path ? `${path} → ${e.name}` : e.name);
            else check(e.ubos, path ? `${path} → ${e.name}` : e.name);
          }
        }
      };
      check(shareholders);
      setIncompleteChains(incomplete);
      setTraceComplete(incomplete.length === 0);

      // Build list of UBOs from corporate shareholders requiring declarations
      const uboOwners = owners.filter(o => o.requiresBiometrics);
      const individualShareholderNames = new Set(
        shareholders.filter(s => s.type === 'individual').map(s => s.name.toLowerCase().trim())
      );
      const uniqueUBOs: UBOWithDeclarations[] = uboOwners
        .filter(o => !individualShareholderNames.has(o.name.toLowerCase().trim()))
        .map(o => ({ name: o.name, effectiveOwnership: o.effectiveOwnership, path: o.path }));
      setTracedUBOs(uniqueUBOs);

      // ── Build KYC persons list (with combined roles for same person) ────────
      if (company) {
        const HONORIFICS = /^(mr\.?|mrs\.?|ms\.?|miss|dr\.?|prof\.?|sir|lord|dame)\s+/i;
        const norm = (n: string) => n.toLowerCase().trim().replace(HONORIFICS, '').trim();
        const applicantEmail = data.email || '';
        const applicantPhone = data.phoneNumber ? `${data.countryCode || ''}${data.phoneNumber}` : '';

        // Fetch fresh biometrics from DB (includes enriched KYC fields written by check-person-verification)
        let freshBiometrics: any[] = data.corporateBiometrics || [];
        const dbSessionId = await getSessionDbId();
        if (dbSessionId) {
          const { data: sess } = await supabase
            .from('onboarding_sessions')
            .select('onboarding_data')
            .eq('id', dbSessionId)
            .maybeSingle();
          if (sess?.onboarding_data) {
            const remoteB = (sess.onboarding_data as any)?.corporateBiometrics;
            if (remoteB?.length) {
              freshBiometrics = remoteB;
              // Seed dbBiometrics immediately so details panel can render on load
              setDbBiometrics(remoteB);
            }
          }
        }

        const existing = freshBiometrics;
        const findEx = (id: string) => existing.find((b: any) => b.id === id);

        type PersonMeta = { name: string; roles: string[]; type: 'director' | 'ubo' | 'authorized'; effectiveOwnership?: number; email: string; phone: string; };
        const personMap = new Map<string, PersonMeta>();

        const upsert = (name: string, role: string, type: PersonMeta['type'], extra: Partial<PersonMeta> = {}) => {
          const key = norm(name);
          if (personMap.has(key)) {
            const existing = personMap.get(key)!;
            if (!existing.roles.includes(role)) existing.roles.push(role);
            if (type === 'director') existing.type = 'director';
          } else {
            personMap.set(key, { name, roles: [role], type, email: '', phone: '', ...extra });
          }
        };

        for (const d of company.directors || []) {
          upsert(d.name, 'Director', 'director', { email: applicantEmail, phone: applicantPhone });
        }
        for (const o of owners.filter(x => x.requiresBiometrics)) {
          const isAlsoDirector = (company.directors || []).some(d => norm(d.name) === norm(o.name));
          upsert(o.name, `${o.effectiveOwnership.toFixed(0)}% Shareholder`, isAlsoDirector ? 'director' : 'ubo', {
            effectiveOwnership: o.effectiveOwnership, email: '', phone: '',
          });
        }
        if (company.authorizedPerson?.firstName) {
          const { firstName, lastName, email, phoneNumber, phoneCountryCode } = company.authorizedPerson;
          const fullName = `${firstName} ${lastName}`.trim();
          const isDir = (company.directors || []).some(d => norm(d.name) === norm(fullName));
          const pEmail = email || '';
          const pPhone = phoneNumber ? `${(phoneCountryCode as any) || ''}${phoneNumber}` : '';
          upsert(fullName, 'Authorised Person', isDir ? 'director' : 'authorized', { email: pEmail, phone: pPhone });
        }

        const list: KycPerson[] = Array.from(personMap.entries()).map(([key, meta]) => {
          const id = meta.type === 'authorized' && !meta.roles.includes('Director')
            ? 'authorized-person'
            : `${meta.type === 'director' ? 'director' : 'ubo'}-${key.replace(/\s+/g, '-')}`;
          const ex = findEx(id);
          const roleLabel = meta.roles.join(' & ');
          // Only restore completed:true if real Sumsub confirmedData exists (prevents phantom "Verified" on reload)
          const isCompleted = (ex?.completed === true && !!ex?.confirmedData) || false;
          // Only restore linkSentVia/linkSentAt if the person is actually verified — for new/fresh sessions
          // these should start blank so buttons don't appear pre-sent (green) without any action taken
          const restoredLinkSentVia = isCompleted ? ex?.linkSentVia : undefined;
          const restoredLinkSentAt = isCompleted ? ex?.linkSentAt : undefined;
          return {
            id, name: meta.name, role: roleLabel, type: meta.type,
            effectiveOwnership: meta.effectiveOwnership,
            email: ex?.email || meta.email,
            phone: ex?.phone || meta.phone,
            completed: isCompleted,
            linkSentVia: restoredLinkSentVia, linkSentAt: restoredLinkSentAt,
            sendPanelOpen: null, saving: false, sending: null,
            // Restore confirmed KYC data from DB so VerificationConfirmScreen pre-fills on resume
            confirmedData: ex?.confirmedData as import('../VerificationConfirmScreen').ConfirmedPersonData | undefined,
          };
        });

        setKycPersons(list);

        // Seed authSumsubExtracted from confirmed data of the authorized person (if already verified)
        // so VerificationConfirmScreen pre-fills all 16 fields when resuming
        if (company?.authorizedPerson?.firstName) {
          const authFullName = `${company.authorizedPerson.firstName} ${company.authorizedPerson.lastName}`.toLowerCase().trim();
          const authEntry = list.find(p => p.name.toLowerCase().trim() === authFullName);
          const cd = authEntry?.confirmedData;
          if (cd) {
            setAuthSumsubExtracted({
              firstName: cd.firstName,
              lastName: cd.lastName,
              dateOfBirth: cd.dateOfBirth,
              address: { street: cd.street, city: cd.city, postalCode: cd.postalCode, country: cd.country },
              document: {
                documentType: cd.documentType,
                documentNumber: cd.documentNumber,
                documentIssueDate: cd.documentIssueDate,
                documentExpiry: cd.documentExpiry,
                documentCountry: cd.documentCountry,
                nationality: cd.nationality,
              },
            });
          }
        }

        // Update DB: merge with existing enriched data — never strip KYC fields already written by check-person-verification
        if (dbSessionId) {
          const { data: latestSess } = await supabase
            .from('onboarding_sessions')
            .select('onboarding_data')
            .eq('id', dbSessionId)
            .maybeSingle();
          if (latestSess) {
            const od = (latestSess.onboarding_data || {}) as Record<string, unknown>;
            const existingB = ((od.corporateBiometrics as any[]) || []);
            // Merge: preserve enriched fields from DB, overlay UI-managed fields
            const mergedPayload = list.map(p => {
              const ex = existingB.find((b: any) => b.id === p.id) || {};
              return {
                ...ex, // preserve enriched KYC fields (firstName, dateOfBirth, nationality, etc.)
                id: p.id, name: p.name,
                type: (p.type === 'authorized' ? 'authorized' : p.type === 'director' ? 'director' : 'shareholder'),
                shareholding: p.effectiveOwnership,
                // Only carry completed:true if real confirmedData exists (prevents phantom verified state)
                completed: ((ex as any)?.completed === true && !!(ex as any)?.confirmedData) ? true : p.completed,
                email: p.email, phone: p.phone,
                linkSentVia: p.linkSentVia, linkSentAt: p.linkSentAt,
              };
            });
            await supabase
              .from('onboarding_sessions')
              .update({ onboarding_data: { ...od, corporateBiometrics: mergedPayload }, updated_at: new Date().toISOString() })
              .eq('id', dbSessionId);
            setDbBiometrics(mergedPayload);
            updateData({ corporateBiometrics: mergedPayload as any });
          }
        } else {
          updateData({ corporateBiometrics: list.map(p => ({
            id: p.id, name: p.name,
            type: (p.type === 'authorized' ? 'authorized' : p.type === 'director' ? 'director' : 'shareholder') as 'authorized' | 'director' | 'shareholder',
            shareholding: p.effectiveOwnership, completed: p.completed,
            email: p.email, phone: p.phone, linkSentVia: p.linkSentVia, linkSentAt: p.linkSentAt,
          })) });
        }
      }

      setIsAnalyzing(false);
    };
    analyze();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Build existing people list
  useEffect(() => {
    if (!company) return;
    const people: typeof existingPeople = [];
    company.directors?.forEach((d, idx) => people.push({ id: `director-${idx}`, name: d.name, type: 'director', role: d.occupation || 'Director' }));
    company.shareholders?.forEach((s, idx) => {
      const isDir = company.directors?.some(d => d.name.toLowerCase().trim() === s.name.toLowerCase().trim());
      if (!isDir) people.push({ id: `shareholder-${idx}`, name: s.name, type: 'shareholder', shareholding: s.shareholding });
    });
    setExistingPeople(people);
  }, [company]);

  // Sync KYC persons to context AND directly to DB to prevent stale-data overwrites.
  // CRITICAL: merges with existing DB record to preserve KYC-enriched fields (firstName, lastName,
  // dateOfBirth, nationality, documentNumber, address, etc.) that were written by check-person-verification.
  const syncBiometrics = async (updated: KycPerson[]) => {
    const dbSessionId = await getSessionDbId();

    // Read DB state first so we can merge enriched fields without losing them
    let existingBiometrics: Record<string, unknown>[] = [];
    if (dbSessionId) {
      const { data: sess } = await supabase
        .from('onboarding_sessions')
        .select('onboarding_data')
        .eq('id', dbSessionId)
        .maybeSingle();
      if (sess?.onboarding_data) {
        existingBiometrics = ((sess.onboarding_data as any)?.corporateBiometrics || []) as Record<string, unknown>[];
      }
    }

    // Build payload: keep KYC-enriched fields from DB, overlay UI-managed fields
    const biometricsPayload = updated.map(p => {
      const existing = existingBiometrics.find((b: any) => b.id === p.id) || {};
      // Merge confirmedData: preserve existing nested object, then overlay with current
      const mergedConfirmedData = {
        ...(existing as any)?.confirmedData,
        ...(p.confirmedData || {}),
      };
      const hasConfirmedData = Object.keys(mergedConfirmedData).length > 0;
      return {
        // Spread DB record first to preserve enriched KYC fields
        ...existing,
        // Spread confirmed data flat (for backwards compat)
        ...(p.confirmedData || {}),
        // Also store confirmedData as nested object so DocumentUploadStep can read it
        ...(hasConfirmedData ? { confirmedData: mergedConfirmedData } : {}),
        // Then overlay UI-managed fields (never downgrade completed:true)
        id: p.id,
        name: p.name,
        type: (p.type === 'authorized' ? 'authorized' : p.type === 'director' ? 'director' : 'shareholder') as 'authorized' | 'director' | 'shareholder',
        shareholding: p.effectiveOwnership,
        completed: (existing as any)?.completed === true ? true : p.completed,
        email: p.email,
        phone: p.phone,
        linkSentVia: p.linkSentVia,
        linkSentAt: p.linkSentAt,
      };
    });

    // Write directly to DB
    if (dbSessionId) {
      const { data: latestSess } = await supabase
        .from('onboarding_sessions')
        .select('onboarding_data')
        .eq('id', dbSessionId)
        .maybeSingle();
      if (latestSess) {
        const od = (latestSess.onboarding_data || {}) as Record<string, unknown>;
        await supabase
          .from('onboarding_sessions')
          .update({ onboarding_data: { ...od, corporateBiometrics: biometricsPayload }, updated_at: new Date().toISOString() })
          .eq('id', dbSessionId);
      }
    }

    // Then update local context
    updateData({ corporateBiometrics: biometricsPayload });
  };

  // Poll session for remote verification completions (every 10s when any link has been sent)
  // Poll for verification status — covers BOTH remote (link sent) and on-device (Sumsub webhook) completions
  useEffect(() => {
    if (!sessionToken) return;
    const hasUnverified = kycPersons.some(p => !p.completed);
    if (!hasUnverified) return;

    const poll = async () => {
      const dbSessionId = await getSessionDbId();
      if (!dbSessionId) return;

      // 1. Check DB session for remotely-completed biometrics
      const { data: sess } = await supabase
        .from('onboarding_sessions')
        .select('onboarding_data')
        .eq('id', dbSessionId)
        .maybeSingle();
      if (!sess) return;
      const remoteBiometrics = (sess.onboarding_data as any)?.corporateBiometrics as Array<{ id: string; completed?: boolean }> | undefined;

      let anyChanged = false;

      setKycPersons(prev => {
        let changed = false;
        const updated = prev.map(p => {
          if (p.completed) return p;
          // Check DB biometrics first
          const remote = remoteBiometrics?.find(r => r.id === p.id);
          if (remote?.completed) { changed = true; return { ...p, completed: true }; }
          return p;
        });
        if (changed) {
          anyChanged = true;
          syncBiometrics(updated);
        }
        return changed ? updated : prev;
      });

      // 2. For persons still unverified, call check-person-verification to ask Sumsub directly
      if (!anyChanged) {
        const unverified = kycPersons.filter(p => !p.completed);
        for (const person of unverified) {
          try {
            const { data: result } = await supabase.functions.invoke('check-person-verification', {
              body: { personId: person.id, sessionId: dbSessionId },
            });
            if (result?.verified) {
              setKycPersons(prev => {
                const updated = prev.map(p => p.id === person.id ? { ...p, completed: true } : p);
                syncBiometrics(updated);
                return updated;
              });
              // Refresh enriched KYC data
              await refreshDbBiometrics();
              break; // Re-poll on next tick for remaining persons
            }
          } catch {
            // Silently ignore — will retry on next poll
          }
        }
      }
    };

    const interval = setInterval(poll, 8000);
    // Also poll immediately once
    poll();
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycPersons, sessionToken]);

  // Toggle send panel
  const toggleSendPanel = (id: string, via: 'email' | 'sms' | 'whatsapp') => {
    setKycPersons(prev => prev.map(p =>
      p.id === id
        ? { ...p, sendPanelOpen: p.sendPanelOpen === via ? null : via }
        : { ...p, sendPanelOpen: null }
    ));
  };

  // Send verification link
  const sendVerificationLink = async (person: KycPerson, via: 'email' | 'sms' | 'whatsapp') => {
    setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, sending: via } : p));
    const baseUrl = 'https://ionboarding.lovable.app';
    // Append a timestamp so each new link generates a FRESH Sumsub applicant.
    // Reusing the same personId would hit the previously-expired applicant and show "Verification expired".
    const linkToken = `${person.id}-${Date.now()}`;
    const verificationUrl = `${baseUrl}/verify?person=${encodeURIComponent(linkToken)}`;
    const firstName = person.name.split(' ')[0];
    const message = `Hi ${firstName}, please complete your identity verification: ${verificationUrl}`;
    try {
      if (via === 'email' && person.email) {
        const dbSessionId = await getSessionDbId();
        await supabase.functions.invoke('send-sumsub-link', {
          body: {
            email: person.email,
            name: person.name,
            verification_url: verificationUrl,
            session_id: dbSessionId,
            // Skip Sumsub applicant creation — the /verify page creates it when the person opens the link
            skip_applicant_creation: true,
            first_name: person.name.split(' ')[0],
            last_name: person.name.split(' ').slice(1).join(' ') || undefined,
          }
        }).catch(() => {});
      } else if (via === 'sms' && person.phone) {
        const { error: smsError } = await supabase.functions.invoke('send-verification-sms', {
          body: { phoneNumber: person.phone, name: person.name, message }
        });
        if (smsError) throw smsError;
      } else if (via === 'whatsapp' && person.phone) {
        const clean = person.phone.replace(/[^\d]/g, '');
        window.open(`https://wa.me/${clean}?text=${encodeURIComponent(message)}`, '_blank');
      }
      const sentAt = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const updated = kycPersons.map(p =>
        p.id === person.id ? { ...p, sending: null, linkSentVia: via, linkSentAt: sentAt, sendPanelOpen: null } : p
      );
      setKycPersons(updated);
      await syncBiometrics(updated);
    } catch {
      setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, sending: null } : p));
    }
  };

  // Save contact details
  const saveContactDetails = async (person: KycPerson) => {
    setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, saving: true } : p));
    try {
      const dbSessionId = await getSessionDbId();
      if (dbSessionId) {
        await supabase.from('application_notes').insert({
          session_id: dbSessionId, admin_user_id: 'system',
          note_text: `Contact updated for ${person.name} — email: ${person.email}, phone: ${person.phone}`,
          is_internal: true,
        }).then(() => {});
      }
      const updated = kycPersons.map(p => p.id === person.id ? { ...p, saving: false } : p);
      setKycPersons(updated);
      await syncBiometrics(updated);
    } catch {
      setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, saving: false } : p));
    }
  };

  // Check if a remote person has been verified
  const checkPersonVerification = async (person: KycPerson) => {
    setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, saving: true } : p));
    try {
      const dbSessionId = await getSessionDbId();

      const { data: result } = await supabase.functions.invoke('check-person-verification', {
        body: { personId: person.id, sessionId: dbSessionId },
      });

      if (result?.verified) {
        const updated = kycPersons.map(p => p.id === person.id ? { ...p, completed: true, saving: false } : p);
        setKycPersons(updated);
        await syncBiometrics(updated);
        // Refresh enriched KYC data from DB
        await refreshDbBiometrics();
      } else {
        setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, saving: false } : p));
        setForceConfirmPerson(person);
      }
    } catch {
      setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, saving: false } : p));
    }
  };

  // Called when user confirms via the branded dialog
  const handleForceConfirm = async () => {
    if (!forceConfirmPerson) return;
    const person = forceConfirmPerson;
    setForceConfirmPerson(null);
    const updated = kycPersons.map(p => p.id === person.id ? { ...p, completed: true } : p);
    setKycPersons(updated);
    await syncBiometrics(updated);
  };

  // Sumsub verification complete → show confirm screen first
  const handleVerificationComplete = async (extractedData?: ExtractedData) => {
    const currentId = verifyingId;
    setVerifyingId(null);

    // If SDK returned meaningful data, use it directly
    if (extractedData?.firstName || extractedData?.document?.documentNumber) {
      setPendingConfirm({ id: currentId!, extractedData });
      return;
    }

    // SDK didn't return data yet — call check-person-verification to extract from Sumsub API
    const dbSessionId = await getSessionDbId();
    if (dbSessionId && currentId) {
      try {
        // This calls the Sumsub API directly and writes enriched KYC data to the DB
        await supabase.functions.invoke('check-person-verification', {
          body: { personId: currentId, sessionId: dbSessionId },
        });

        // Small delay for DB write to propagate
        await new Promise(r => setTimeout(r, 2000));

        // Read enriched data back from DB
        const { data: sess } = await supabase
          .from('onboarding_sessions')
          .select('onboarding_data')
          .eq('id', dbSessionId)
          .maybeSingle();

        if (sess?.onboarding_data) {
          const bio = ((sess.onboarding_data as any)?.corporateBiometrics || []) as any[];
          const personData = bio.find((b: any) => b.id === currentId);

          if (personData && (personData.firstName || personData.documentNumber)) {
            const dbExtracted: ExtractedData = {
              firstName: personData.firstName,
              lastName: personData.lastName,
              dateOfBirth: personData.dateOfBirth,
              address: personData.address,
              document: {
                documentType: personData.documentType,
                documentNumber: personData.documentNumber,
                documentExpiry: personData.documentExpiry,
                documentIssueDate: personData.documentIssueDate,
                documentCountry: personData.documentCountry,
                nationality: personData.nationality,
              },
            };
            setPendingConfirm({ id: currentId!, extractedData: dbExtracted });
            setDbBiometrics(bio);
            updateData({ corporateBiometrics: bio as any });
            return;
          }
        }
      } catch (e) {
        console.error('Failed to fetch enriched data from DB:', e);
      }
    }

    // Final fallback — show confirm screen with whatever we have
    setPendingConfirm({ id: currentId!, extractedData });
  };

  // User confirmed on the confirm screen — save all 16 fields and mark complete
  const handleConfirmComplete = async (confirmed: import('../VerificationConfirmScreen').ConfirmedPersonData) => {
    if (!pendingConfirm) return;
    const currentId = pendingConfirm.id;
    setPendingConfirm(null);
    const updated = kycPersons.map(p =>
      p.id === currentId
        ? { ...p, completed: true, sendPanelOpen: null, confirmedData: confirmed,
            email: confirmed.email || p.email, phone: confirmed.phone || p.phone }
        : p
    );
    setKycPersons(updated);
    await syncBiometrics(updated);
    setTimeout(async () => {
      const dbSessionId = await getSessionDbId();
      if (dbSessionId && currentId) {
        await supabase.functions.invoke('check-person-verification', {
          body: { personId: currentId, sessionId: dbSessionId },
        });
        await refreshDbBiometrics();
      }
    }, 2000);
  };


  // Reset Sumsub ID refs when verifyingId clears so the next open always gets a fresh ID
  useEffect(() => {
    if (!verifyingId) {
      lastVerifyingIdRef.current = null;
      sumsubPersonIdRef.current = null;
    }
  }, [verifyingId]);

  const handleBack = useCallback(() => {
    if (verifyingId) { setVerifyingId(null); return true; }
    if (stage === 'ubo-declarations') {
      if (activeUBOIndex > 0) { setActiveUBOIndex(prev => prev - 1); return true; }
      setStage('ubo-trace');
      return true;
    }
    return false;
  }, [stage, activeUBOIndex, verifyingId]);

  useEffect(() => {
    setCustomBackHandler(handleBack);
    return () => setCustomBackHandler(null);
  }, [handleBack, setCustomBackHandler]);

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-muted-foreground">Company data not found.</p>
        </div>
      </div>
    );
  }

  if (isAnalyzing) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
        <p className="text-muted-foreground">Analysing ownership structure...</p>
      </div>
    );
  }

  // ── Helpers ──
  const togglePath = (path: string) => {
    const s = new Set(expandedPaths);
    if (s.has(path)) s.delete(path); else s.add(path);
    setExpandedPaths(s);
  };

  const updateAuthField = (field: string, value: string | boolean) => {
    if (field.startsWith('address.')) {
      const af = field.split('.')[1] as keyof AuthorizedPerson['address'];
      setAuthPerson(prev => ({ ...prev, address: { ...prev.address, [af]: value as string } }));
    } else {
      setAuthPerson(prev => ({ ...prev, [field]: value }));
    }
  };

  const updateUBODeclarations = (index: number, updates: Partial<PersonDeclarationData>) => {
    setTracedUBOs(prev => {
      const n = [...prev];
      n[index] = { ...n[index], ...updates };
      return n;
    });
  };

  const declarationComplete = (p: PersonDeclarationData) =>
    p.isPEP !== undefined && (p.taxResidencies?.length ?? 0) > 0 && p.isUSResident !== undefined;

  const isAuthBasicValid = authPerson.firstName.trim() && authPerson.lastName.trim() && authPerson.jobTitle.trim();
  const isAuthConfirmValid = authPerson.firstName.trim() && authPerson.lastName.trim() && authPerson.email.trim() && authPerson.jobTitle.trim() && authPerson.address.street.trim() && authPerson.address.city.trim();
  const isAuthDeclarationsValid = declarationComplete(authPerson);

  const selectExistingPerson = (ep: typeof existingPeople[0]) => {
    setSelectedExistingId(ep.id);
    const parts = ep.name.split(' ');
    let nationality = '';
    if (ep.type === 'director') {
      const idx = parseInt(ep.id.split('-')[1]);
      nationality = company.directors?.[idx]?.nationality || '';
    }
    const countryData = company?.countryCode ? countries.find(c => c.code === company.countryCode) : null;

    // Find existing verified KYC data for this person
    const epNameNorm = ep.name.toLowerCase().trim();
    const verifiedPerson = kycPersons.find(p => p.name.toLowerCase().trim() === epNameNorm && p.completed);
    const cd = verifiedPerson?.confirmedData;

    // Convert ConfirmedPersonData → ExtractedData so VerificationConfirmScreen pre-fills
    if (cd) {
      const extracted: import('../SumsubVerification').ExtractedData = {
        firstName: cd.firstName,
        lastName: cd.lastName,
        dateOfBirth: cd.dateOfBirth,
        address: { street: cd.street, city: cd.city, postalCode: cd.postalCode, country: cd.country },
        document: {
          documentType: cd.documentType,
          documentNumber: cd.documentNumber,
          documentIssueDate: cd.documentIssueDate,
          documentExpiry: cd.documentExpiry,
          documentCountry: cd.documentCountry,
          nationality: cd.nationality,
        },
      };
      setAuthSumsubExtracted(extracted);
      // Also pre-fill authPerson with confirmed data
      setAuthPerson(prev => ({
        ...prev,
        firstName: cd.firstName || parts[0] || '',
        lastName: cd.lastName || parts.slice(1).join(' ') || '',
        jobTitle: ep.role || prev.jobTitle,
        dateOfBirth: cd.dateOfBirth || '',
        nationality: cd.nationality || resolveCountryName(nationality) || nationality,
        address: {
          street: cd.street || '',
          city: cd.city || '',
          postalCode: cd.postalCode || '',
          country: cd.country || countryData?.name || prev.address.country,
        },
        isDirector: ep.type === 'director',
        isExistingPerson: true,
      } as AuthorizedPerson));
    } else {
      setAuthPerson(prev => ({
        ...prev,
        firstName: parts[0] || '',
        lastName: parts.slice(1).join(' ') || '',
        jobTitle: ep.role || '',
        nationality: resolveCountryName(nationality) || nationality,
        address: { ...prev.address, country: countryData?.name || prev.address.country },
        isDirector: ep.type === 'director',
        isExistingPerson: true,
      } as AuthorizedPerson));
    }

    setAuthStage(verifiedPerson ? 'confirm' : 'sumsub');
  };

  const handleSaveFinal = () => {
    updateData({
      corporateCompany: {
        ...company,
        authorizedPerson: authPerson as any,
      },
      corporateTaxResidencies: [
        // Merge UBO declarations
        ...tracedUBOs.map((u, i) => ({
          id: `ubo-traced-${i}`,
          name: u.name,
          type: 'shareholder' as const,
          taxResidencies: u.taxResidencies || [],
          isUSResident: u.isUSResident || false,
          giinNumber: u.giinNumber,
          completed: true,
        })),
        // Authorized person
        {
          id: 'authorized',
          name: `${authPerson.firstName} ${authPerson.lastName}`,
          type: 'authorized' as const,
          taxResidencies: authPerson.taxResidencies || [],
          isUSResident: authPerson.isUSResident || false,
          giinNumber: authPerson.giinNumber,
          completed: true,
        },
        // Preserve existing from directors/shareholders step
        ...(data.corporateTaxResidencies || []).filter(t => t.type !== 'authorized' && !tracedUBOs.some(u => u.name === t.name)),
      ],
      corporatePepDeclarations: [
        ...tracedUBOs.map((u, i) => ({
          id: `ubo-traced-${i}`,
          name: u.name,
          type: 'shareholder' as const,
          completed: true,
          isPEP: u.isPEP || false,
          pepDetails: u.pepDetails,
        })),
        {
          id: 'authorized',
          name: `${authPerson.firstName} ${authPerson.lastName}`,
          type: 'authorized' as const,
          completed: true,
          isPEP: authPerson.isPEP || false,
          pepDetails: authPerson.pepDetails,
        },
        ...(data.corporatePepDeclarations || []).filter(p => p.type !== 'authorized' && !tracedUBOs.some(u => u.name === p.name)),
      ],
    });
    goToNextStep();
  };

  const renderField = (label: string, value: string, fieldKey: string, placeholder?: string, required?: boolean) => {
    const isEditing = editingField === fieldKey;
    const isEmpty = !value.trim();
    const showRequired = required && isEmpty;
    return (
      <div className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
        <span className={`text-sm ${showRequired ? 'text-destructive' : 'text-muted-foreground'}`}>
          {label}{required ? '*' : ''}:
        </span>
        {isEditing ? (
          <Input value={value} onChange={e => updateAuthField(fieldKey, e.target.value)}
            onBlur={() => setEditingField(null)} onKeyDown={e => e.key === 'Enter' && setEditingField(null)}
            className="w-1/2 h-7 text-right text-sm" placeholder={placeholder} autoFocus />
        ) : (
          <span className={`font-medium text-right cursor-pointer hover:text-primary transition-colors text-sm ${showRequired ? 'text-destructive/70' : ''}`}
            onClick={() => setEditingField(fieldKey)}>
            {value || <span className={showRequired ? 'text-destructive/70 italic' : 'text-muted-foreground italic'}>{placeholder || 'Click to edit'}</span>}
          </span>
        )}
      </div>
    );
  };

  const renderOwnershipTree = (entities: (Shareholder | UBO)[], parentPath = '', depth = 0): JSX.Element[] =>
    entities.map((entity, index) => {
      const currentPath = parentPath ? `${parentPath}-${index}` : `${index}`;
      const isExpanded = expandedPaths.has(currentPath);
      const hasChildren = entity.type === 'company' && entity.ubos && entity.ubos.length > 0;
      const isIncomplete = entity.type === 'company' && (!entity.ubos || entity.ubos.length === 0);
      return (
        <div key={currentPath} className={depth > 0 ? 'ml-4 border-l-2 border-border/50 pl-3' : ''}>
          <div className={`flex items-center gap-2 py-2 ${hasChildren ? 'cursor-pointer' : ''}`}
            onClick={() => hasChildren && togglePath(currentPath)}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${entity.type === 'company' ? 'bg-accent/10' : 'bg-success/10'}`}>
              {entity.type === 'company'
                ? <Building2 className="w-4 h-4 text-accent" />
                : <User className="w-4 h-4 text-success" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{entity.name}</p>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">{entity.shareholding}%</Badge>
                {isIncomplete && <Badge variant="destructive" className="text-xs">UBOs Unknown</Badge>}
              </div>
            </div>
            {hasChildren && (isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />)}
          </div>
          {hasChildren && isExpanded && <div className="pb-2">{renderOwnershipTree(entity.ubos!, currentPath, depth + 1)}</div>}
        </div>
      );
    });

  // ══════════════════════════════════════════════════════════════
  // STAGE: UBO TRACE — Ownership tree + ALL Persons requiring Sumsub
  // ══════════════════════════════════════════════════════════════
  if (stage === 'ubo-trace') {
    // Confirm screen after Sumsub
    if (pendingConfirm) {
      const confirmPerson = kycPersons.find(p => p.id === pendingConfirm.id);
      const nameParts = (confirmPerson?.name || '').trim().split(/\s+/);
      const confirmTaxNumber =
        (confirmPerson?.confirmedData as any)?.taxNumber ||
        ((data.corporateTaxResidencies || []) as any[]).find(
          (entry: any) => entry.name?.toLowerCase().trim() === (confirmPerson?.name || '').toLowerCase().trim()
        )?.taxResidencies?.[0]?.tin ||
        '';
      return (
        <VerificationConfirmScreen
          personName={confirmPerson?.name || ''}
          knownFirstName={nameParts[0]}
          knownLastName={nameParts.slice(1).join(' ')}
          personRole={confirmPerson?.role}
          email={confirmPerson?.email || data.email || ''}
          phone={confirmPerson?.phone || `${data.countryCode || ''}${data.phoneNumber || ''}`}
          extractedData={pendingConfirm.extractedData}
          initialTaxNumber={confirmTaxNumber}
          onConfirm={handleConfirmComplete}
        />
      );
    }

    // Inline Sumsub verification takes over the full screen
    if (verifyingId) {
      const person = kycPersons.find(p => p.id === verifyingId);
      // Generate a stable unique ID for this verification session.
      // CRITICAL: Store in sessionStorage keyed by person.id so the ID survives
      // React re-renders, component remounts, AND Sumsub's token refresh callbacks.
      // If we generate a new ID on remount, the token refresh uses a different userId
      // and Sumsub shows "Verification expired" / "Access denied" on the phone.
      // Generate a fresh ID each time verifyingId changes (new verification attempt).
      // Stable within the same attempt via ref — no sessionStorage caching.
      // Always generate a fresh ID for a new verification session open.
      // lastVerifyingIdRef is reset to null when verifyingId goes null (back button),
      // so re-opening the same person always creates a new Sumsub applicant.
      if (lastVerifyingIdRef.current !== verifyingId) {
        lastVerifyingIdRef.current = verifyingId;
        sumsubPersonIdRef.current = person?.id ? `${person.id}-${Date.now()}` : null;
      }
      const stablePersonId = sumsubPersonIdRef.current ?? undefined;
      return (
        <div className="onboarding-container animate-slide-in">
          <div className="flex-1 flex flex-col p-6 max-w-md mx-auto w-full">
            <div className="pt-16 pb-6">
              <div className="flex flex-wrap gap-1.5 mb-4">
                {person?.role?.split(' & ').map((r) => (
                  <span key={r} className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getRolePillClass(r)}`}>{r}</span>
                ))}
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-1">Verify {person?.name}</h1>
              <p className="text-muted-foreground text-sm">Complete identity verification to continue.</p>
            </div>
            <SumsubVerification
              onComplete={handleVerificationComplete}
              onError={(err) => {
                setVerifyingId(null);
                toast({
                  title: 'Verification issue',
                  description: /expired|denied|blocked/i.test(err)
                    ? 'If verification expired, please try again — a fresh session will be created automatically.'
                    : (err || 'Verification could not be completed. Please try again.'),
                  variant: 'destructive',
                });
              }}
              email={person?.email || data.email || undefined}
              phone={person?.phone || `${data.countryCode}${data.phoneNumber}`}
              includePOA={true}
              personName={person?.name}
              personId={stablePersonId}
            />
          </div>
        </div>
      );
    }

    const allVerified = kycPersons.length > 0 && kycPersons.every(p => p.completed);
    const verifiedCount = kycPersons.filter(p => p.completed).length;

    return (
      <>
      <div className="min-h-screen bg-background flex flex-col pt-20 pb-32 px-4 sm:px-6">

        <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
          <div className="animate-fade-in-up space-y-5">

            {/* Title */}
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold tracking-[0.15em] mb-1">VERIFICATION</h1>
              <p className="text-muted-foreground text-xs">Identity verification for all stakeholders</p>
              <div className="flex justify-center mt-3">
                <Badge variant={traceComplete ? 'default' : 'destructive'} className={traceComplete ? 'bg-success' : ''}>
                  {traceComplete ? <><Check className="w-3 h-3 mr-1" />Trace Complete</> : 'Incomplete — Manual Review Required'}
                </Badge>
              </div>
            </div>

            {/* ── Identity Verification Section ── */}
            {kycPersons.length > 0 && (
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                {/* Header with progress */}
                <div className="px-4 pt-4 pb-3 border-b border-border">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold flex items-center gap-2">
                      <Shield className="w-4 h-4 text-primary" />
                      Identity Verification
                    </p>
                     <span className={cn(
                       'text-xs font-semibold px-2 py-0.5 rounded-full',
                       allVerified ? 'bg-green-500/15 text-green-500' : 'bg-yellow-500/15 text-yellow-500'
                     )}>
                      {verifiedCount}/{kycPersons.length} verified
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn('h-full rounded-full transition-all duration-500', allVerified ? 'bg-green-500' : 'bg-yellow-500')}
                      style={{ width: kycPersons.length > 0 ? `${(verifiedCount / kycPersons.length) * 100}%` : '0%' }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {allVerified ? 'All verifications complete ✓' : 'Tap a person to verify on this device, or send a link'}
                  </p>
                </div>

                {/* Person rows */}
                <div className="divide-y divide-border">
                  {kycPersons.map(person => {
                    const pb = (data.corporateBiometrics || []).find((b: any) => b.id === person.id) as any;
                    return (
                    <div key={person.id}>
                      {/* Person row */}
                      <div className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {/* Status icon / tap to verify */}
                          <button
                            onClick={() => !person.completed && setVerifyingId(person.id)}
                            className={cn(
                              'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all',
                              person.completed
                                ? 'bg-success/15 cursor-default'
                                : 'bg-primary/10 hover:bg-primary/20 active:scale-95 cursor-pointer'
                            )}
                          >
                            {person.completed
                              ? <BadgeCheck className="w-5 h-5 text-success" />
                              : <Fingerprint className="w-5 h-5 text-primary" />}
                          </button>

                          {/* Name & role */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{person.name}</p>
                            <div className="flex items-center gap-1 flex-wrap mt-0.5">
                              {person.role.split(' & ').map((r) => (
                                <span key={r} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getRolePillClass(r)}`}>{r}</span>
                              ))}
                              {person.completed && <Badge className="text-xs py-0 bg-success/15 text-success border-success/30">Verified ✓</Badge>}
                            </div>
                          </div>

                          {/* Send link buttons — only if not verified */}
                          {!person.completed && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => toggleSendPanel(person.id, 'email')}
                                      className={cn(
                                        'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                                        person.linkSentVia === 'email'
                                          ? 'bg-green-500/20 text-green-500 border border-green-500/40'
                                          : person.sendPanelOpen === 'email'
                                            ? 'bg-primary/20 text-primary'
                                            : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                                      )}>
                                      <Mail className="w-3.5 h-3.5" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p>{person.linkSentVia === 'email' ? `Sent at ${person.linkSentAt}` : 'Send by Email'}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => toggleSendPanel(person.id, 'sms')}
                                      className={cn(
                                        'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                                        person.linkSentVia === 'sms'
                                          ? 'bg-green-500/20 text-green-500 border border-green-500/40'
                                          : person.sendPanelOpen === 'sms'
                                            ? 'bg-primary/20 text-primary'
                                            : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                                      )}>
                                      <MessageSquare className="w-3.5 h-3.5" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p>{person.linkSentVia === 'sms' ? `Sent at ${person.linkSentAt}` : 'Send by SMS'}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => toggleSendPanel(person.id, 'whatsapp')}
                                      className={cn(
                                        'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                                        person.linkSentVia === 'whatsapp'
                                          ? 'bg-green-500/20 text-green-500 border border-green-500/40'
                                          : person.sendPanelOpen === 'whatsapp'
                                            ? 'bg-green-500/20 text-green-500'
                                            : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                                      )}>
                                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                      </svg>
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p>{person.linkSentVia === 'whatsapp' ? `Sent at ${person.linkSentAt}` : 'Send by WhatsApp'}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          )}
                        </div>

                          {/* Tap hint / refresh row */}
                        {!person.completed && (
                          <div className="mt-2 flex gap-2">
                            <button
                              onClick={() => setVerifyingId(person.id)}
                              className="flex-1 py-2 rounded-lg bg-primary/5 hover:bg-primary/10 border border-primary/20 text-xs text-primary font-medium transition-colors flex items-center justify-center gap-1.5"
                            >
                              <Fingerprint className="w-3.5 h-3.5" /> Verify on this device
                            </button>
                            {person.linkSentVia && (
                              <button
                                onClick={() => checkPersonVerification(person)}
                                disabled={person.saving}
                                title="Check if remote verification is complete"
                                className="py-2 px-3 rounded-lg bg-muted/50 hover:bg-muted border border-border text-xs text-muted-foreground font-medium transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                              >
                                {person.saving
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <RefreshCw className="w-3.5 h-3.5" />}
                                Check
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Send panel — Email */}
                      {person.sendPanelOpen === 'email' && (
                        <div className="px-4 pb-3 bg-muted/20 border-t border-border/50">
                          <p className="text-xs font-medium text-foreground pt-3 mb-2 flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-primary" /> Send verification link by email
                          </p>
                          <Input
                            type="email"
                            value={person.email}
                            onChange={e => setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, email: e.target.value } : p))}
                            placeholder="Enter email address"
                            className="h-9 text-sm mb-2"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => saveContactDetails(person)} disabled={person.saving}
                              className="text-xs h-8 flex-1">
                              {person.saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                            </Button>
                            <Button size="sm" onClick={() => sendVerificationLink(person, 'email')}
                              disabled={!person.email || !!person.sending}
                              className="text-xs h-8 flex-1">
                              {person.sending === 'email' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                              Send Link
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Send panel — SMS */}
                      {person.sendPanelOpen === 'sms' && (
                        <div className="px-4 pb-3 bg-muted/20 border-t border-border/50">
                          <p className="text-xs font-medium text-foreground pt-3 mb-2 flex items-center gap-1.5">
                            <MessageSquare className="w-3.5 h-3.5 text-primary" /> Send verification link by SMS
                          </p>
                          <PhoneInputSmall
                            value={person.phone}
                            onChange={v => setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, phone: v } : p))}
                          />
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" variant="outline" onClick={() => saveContactDetails(person)} disabled={person.saving}
                              className="text-xs h-8 flex-1">
                              {person.saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                            </Button>
                            <Button size="sm" onClick={() => sendVerificationLink(person, 'sms')}
                              disabled={!person.phone || !!person.sending}
                              className="text-xs h-8 flex-1">
                              {person.sending === 'sms' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                              Send SMS
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Send panel — WhatsApp */}
                      {person.sendPanelOpen === 'whatsapp' && (
                        <div className="px-4 pb-3 bg-muted/20 border-t border-border/50">
                          <p className="text-xs font-medium text-foreground pt-3 mb-2 flex items-center gap-1.5">
                            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current text-success" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            Send verification link via WhatsApp
                          </p>
                          <PhoneInputSmall
                            value={person.phone}
                            onChange={v => setKycPersons(prev => prev.map(p => p.id === person.id ? { ...p, phone: v } : p))}
                          />
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" variant="outline" onClick={() => saveContactDetails(person)} disabled={person.saving}
                              className="text-xs h-8 flex-1">
                              {person.saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                            </Button>
                            <Button size="sm" onClick={() => sendVerificationLink(person, 'whatsapp')}
                              disabled={!person.phone || !!person.sending}
                              className="text-xs h-8 flex-1 bg-success hover:bg-success/90 text-success-foreground border-0">
                              {person.sending === 'whatsapp' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                              WhatsApp
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* KYC details panel — shown when person is verified */}
                      {person.completed && (() => {
                        // Use dbBiometrics (DB-sourced, has enriched KYC fields) with context fallback
                        const pb = (dbBiometrics.length > 0 ? dbBiometrics : (data.corporateBiometrics || [])).find((b: any) => b.id === person.id) as any;
                        if (!pb) return null;
                        const hasDetails = pb.dateOfBirth || pb.nationality || pb.documentNumber || pb.documentType || pb.address?.street;
                        if (!hasDetails) return null;
                        const fmtDate = (d: string) => {
                          if (!d) return '';
                          try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
                        };
                        const addr = pb.address;
                        return (
                          <div className="mx-4 mb-3 rounded-lg border border-success/20 bg-success/5 p-3 space-y-1.5 text-xs">
                            {(pb.firstName || pb.lastName) && (
                              <div className="flex justify-between"><span className="text-muted-foreground">Full Name</span><span className="font-medium">{[pb.firstName, pb.lastName].filter(Boolean).join(' ')}</span></div>
                            )}
                            {pb.dateOfBirth && <div className="flex justify-between"><span className="text-muted-foreground">Date of Birth</span><span className="font-medium">{fmtDate(pb.dateOfBirth)}</span></div>}
                            {pb.nationality && <div className="flex justify-between"><span className="text-muted-foreground">Nationality</span><span className="font-medium">{pb.nationality}</span></div>}
                            
                            {pb.documentType && <div className="flex justify-between"><span className="text-muted-foreground">Document</span><span className="font-medium">{formatLabel(pb.documentType)}</span></div>}
                            {pb.documentNumber && <div className="flex justify-between"><span className="text-muted-foreground">ID Number</span><span className="font-medium font-mono">{pb.documentNumber}</span></div>}
                            {pb.documentExpiry && <div className="flex justify-between"><span className="text-muted-foreground">Expiry Date</span><span className="font-medium">{fmtDate(pb.documentExpiry)}</span></div>}
                            {pb.documentIssueDate && <div className="flex justify-between"><span className="text-muted-foreground">Issue Date</span><span className="font-medium">{fmtDate(pb.documentIssueDate)}</span></div>}
                            {pb.documentCountry && <div className="flex justify-between"><span className="text-muted-foreground">Issued By</span><span className="font-medium">{pb.documentCountry}</span></div>}
                            {addr?.street && <div className="flex justify-between gap-4"><span className="text-muted-foreground shrink-0">Address</span><span className="font-medium text-right">{[addr.street, addr.city, addr.postalCode, addr.country].filter(Boolean).join(', ')}</span></div>}
                          </div>
                        );
                      })()}
                    </div>
                  );
                  })}
                </div>
              </div>
            )}

            {/* Incomplete chains warning */}
            {incompleteChains.length > 0 && (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
                <p className="text-sm font-medium text-destructive mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Incomplete Ownership Chains
                </p>
                <ul className="space-y-1">
                  {incompleteChains.map((chain, i) => (
                    <li key={i} className="text-xs flex items-center gap-2">
                      <Building2 className="w-3 h-3 text-destructive" />{chain}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          </div>
        </div>

        {/* Fixed Continue button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
          <div className="max-w-md mx-auto">
            <Button
              onClick={() => {
                if (!allVerified) return;
                if (tracedUBOs.length > 0) { setStage('ubo-declarations'); setActiveUBOIndex(0); return; }
                goToNextStep();
              }}
              disabled={!allVerified}
              className={cn(
                'w-full h-14 text-base font-semibold rounded-full transition-all',
                allVerified
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-muted text-muted-foreground cursor-not-allowed opacity-60'
              )}
            >
              {allVerified
                ? <>Continue <ChevronRight className="w-5 h-5 ml-1" /></>
                : `${kycPersons.length - verifiedCount} verification${kycPersons.length - verifiedCount !== 1 ? 's' : ''} remaining`
              }
            </Button>
            {!traceComplete && (
              <p className="text-xs text-center text-muted-foreground mt-2">Application will require manual review due to incomplete trace</p>
            )}
          </div>
      </div>
    </div>

      {/* Branded confirmation dialog — replaces window.confirm */}
      <Dialog open={!!forceConfirmPerson} onOpenChange={open => { if (!open) setForceConfirmPerson(null); }}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-primary" />
              Confirm Verification
            </DialogTitle>
            <DialogDescription className="pt-1">
              We could not automatically retrieve the verification result from our identity provider.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-foreground">
            Did <span className="font-semibold">{forceConfirmPerson?.name}</span> complete the identity verification?
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setForceConfirmPerson(null)}>
              Not yet
            </Button>
            <Button className="w-full sm:w-auto" onClick={handleForceConfirm}>
              <Check className="w-4 h-4 mr-1.5" /> Yes, mark as verified
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════
  if (stage === 'ubo-declarations') {
    const currentUBO = tracedUBOs[activeUBOIndex];
    const isLast = activeUBOIndex === tracedUBOs.length - 1;
    return (
      <div className="min-h-screen bg-background flex flex-col pt-20 pb-28 px-4 sm:px-6">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
          <div className="animate-fade-in-up">
            <h1 className="text-xl sm:text-2xl font-bold text-center tracking-[0.15em] mb-1">UBO DECLARATIONS</h1>
            <p className="text-muted-foreground text-center text-xs mb-2">
              Person {activeUBOIndex + 1} of {tracedUBOs.length}
            </p>

            {/* Progress dots */}
            <div className="flex gap-2 justify-center mb-4">
              {tracedUBOs.map((u, i) => (
                <button key={i} onClick={() => setActiveUBOIndex(i)}
                  className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                    i === activeUBOIndex ? 'bg-primary text-primary-foreground'
                    : declarationComplete(u) ? 'bg-success text-white'
                    : 'bg-muted text-muted-foreground'
                  }`}>
                  {declarationComplete(u) ? <Check className="w-4 h-4 mx-auto" /> : i + 1}
                </button>
              ))}
            </div>

            {/* Person card */}
            <div className="rounded-xl border border-primary bg-primary/5 p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{currentUBO.name}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">UBO</Badge>
                    <Badge variant="outline" className="text-xs">{currentUBO.effectiveOwnership.toFixed(1)}% effective</Badge>
                  </div>
                  {currentUBO.path.length > 1 && (
                    <p className="text-xs text-muted-foreground mt-0.5">{currentUBO.path.join(' → ')}</p>
                  )}
                </div>
              </div>
            </div>

            <InlineDeclarations
              person={{ ...currentUBO, name: currentUBO.name }}
              onChange={updates => updateUBODeclarations(activeUBOIndex, updates)}
            />
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
          <div className="max-w-md mx-auto">
            <Button onClick={() => { if (!isLast) { setActiveUBOIndex(prev => prev + 1); } else { goToNextStep(); } }}
              className="w-full h-14 text-base font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {isLast ? 'Continue' : 'Next UBO'}
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
}
}
